/// <amd-module name="Environments/Client/EnvironmentService" />
import { IVssService, Services, VssService, IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { getEnvironmentsClient, IEnvironmentsRestClient } from "Environments/Client/RestClient/Environments";
import { getServiceEndpointClient, IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import {
    EnvironmentInstance,
    EnvironmentCreateParameter,
    EnvironmentUpdateParameter,
    EnvironmentExpands,
    KubernetesResource,
    EnvironmentResourceType,
    VirtualMachineGroupCreateParameters,
    VirtualMachineGroup,
    EnvironmentResource,
    KubernetesResourcePatchParameters,
    KubernetesResourceCreateParametersExistingEndpoint,
} from "DistributedTask/Client/Contracts/TaskAgent";

import {
    ServiceEndpoint,
    AzureSubscriptionQueryResult,
    ServiceEndpointRequestResult,
    ServiceEndpointRequest
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { DataProviderIds } from "Environments/Client/Constants";
import { IEnvironmentPageData, ICommitsResult, IWorkitemsResult, IEnvironmentResult, IEnvironmentObject } from "Environments/Client/EnvironmentContracts";
import { PageContextHelper } from "Environments/Client/PageContextHelper";

export interface IEnvironmentService extends IVssService {
    addEnvironment(environmentCreateParameter: EnvironmentCreateParameter): Promise<EnvironmentInstance>;
    deleteEnvironment(environmentId: number): Promise<void>;
    getEnvironmentFromPageData(): IEnvironmentObject | undefined;
    getEnvironmentById(environmentId: number, expands: EnvironmentExpands): Promise<EnvironmentInstance>;
    updateEnvironment(environmentUpdateParameter: EnvironmentUpdateParameter, environmentId: number): Promise<EnvironmentInstance>;
    getServiceEndpoints(type?: string): Promise<ServiceEndpoint[]>;
    addKubernetesResource(kubernetesResourceCreateParameters: KubernetesResourceCreateParametersExistingEndpoint, environmentId: number): Promise<KubernetesResource>;
    //addKubernetesResource(kubernetesResourceCreateParameters: KubernetesResourceCreateParametersNewEndpoint, environmentId: number): Promise<KubernetesResource>;
    updateKubernetesResource(kubernetesResourcePatchParameters: KubernetesResourcePatchParameters, environmentId: number): Promise<KubernetesResource>;
    addVirtualMachinesResource(virtualMachinesResourceCreateParameters: VirtualMachineGroupCreateParameters, environmentId: number): Promise<VirtualMachineGroup>;
    getServiceEndpoint(endpointId: string): Promise<ServiceEndpoint>;
    createServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint>;
    updateServiceEndpoint(endpoint: ServiceEndpoint, endpointId: string): Promise<ServiceEndpoint>;
    deleteServiceEndpoint(endpointId: string): Promise<void>;
    getAzureSubscriptions(): Promise<AzureSubscriptionQueryResult>;
    executeServiceEndpointRequestResult(serviceEndpointRequest: ServiceEndpointRequest, endpointId: string): Promise<ServiceEndpointRequestResult>;
    getAADTenantId(): Promise<string>;
    deleteResource(environmentId: number, resourceId: number, resourceType: EnvironmentResourceType): Promise<void>;
    getCommits(top?: string): ICommitsResult | undefined;
    getCommitsAsync(top?: string): Promise<ICommitsResult>;
    getWorkitems(top?: string): IWorkitemsResult | undefined;
    getWorkitemsAsync(top?: string): Promise<IWorkitemsResult>;
    updateEnvironmentResourceTags(resourceId: number, resourceType: EnvironmentResourceType, newTagsSet: string[]): Promise<EnvironmentResource>;
    getEnvironmentScopedPAT(environmentId: number): Promise<string>;
    deleteEnvironmentResource (resourceId: number, resourceType: EnvironmentResourceType): Promise<void>
}

class EnvironmentService extends VssService implements IEnvironmentService {
    public addEnvironment(environmentCreateParameter: EnvironmentCreateParameter): Promise<EnvironmentInstance> {
        return this._getClient().addEnvironment(environmentCreateParameter, this._getProjectName());
    }

    public deleteEnvironment(environmentId: number): Promise<void> {
        return this._getClient().deleteEnvironment(this._getProjectName(), environmentId);
    }

    public getEnvironmentById(environmentId: number, expands: EnvironmentExpands = EnvironmentExpands.None): Promise<EnvironmentInstance> {
        return this._getClient().getEnvironmentById(this._getProjectName(), environmentId, expands);
    }

    public getEnvironmentFromPageData(): IEnvironmentObject | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data: IEnvironmentResult | undefined = contributionService.getData<IEnvironmentResult>("ms.vss-environments-web.environment-resources-view-data-provider");

        return !!data ? data.environment : undefined;
    }

    public updateEnvironment(environmentUpdateParameter: EnvironmentUpdateParameter, environmentId: number): Promise<EnvironmentInstance> {
        return this._getClient().updateEnvironment(environmentUpdateParameter, this._getProjectName(), environmentId);
    }

    public getServiceEndpoints(type?: string): Promise<ServiceEndpoint[]> {
        return this._getServiceEndpointsClient().getServiceEndpoints(this._getProjectName(), type);
    }

    public addKubernetesResource(kubernetesResourceCreateParameters: KubernetesResourceCreateParametersExistingEndpoint, environmentId: number): Promise<KubernetesResource> {
        return this._getClient().addKubernetesResource(kubernetesResourceCreateParameters, this._getProjectName(), environmentId);
    }

    // public addKubernetesResource(kubernetesResourceCreateParameters: KubernetesResourceCreateParametersNewEndpoint, environmentId: number): Promise<KubernetesResource> {
    //     return this._getClient().addKubernetesResourceNewEndpoint(kubernetesResourceCreateParameters, this._getProjectName(), environmentId);
    // }

    public updateKubernetesResource(patchRequest: KubernetesResourcePatchParameters, environmentId: number): Promise<KubernetesResource> {
        return getEnvironmentsClient(this.pageContext).updateKubernetesResource(patchRequest, this._getProjectName(), environmentId);
    }

    public addVirtualMachinesResource(virtualMachinesResourceCreateParameters: VirtualMachineGroupCreateParameters, environmentId: number): Promise<VirtualMachineGroup> {
        return this._getClient().addVirtualMachineGroup(virtualMachinesResourceCreateParameters, this._getProjectName(), environmentId);
    }

    public getEnvironmentScopedPAT(environmentId: number): Promise<string> {
        return getEnvironmentsClient(this.pageContext).generateEnvironmentAccessToken(this._getProjectName(), environmentId);
    }

    public deleteResource(environmentId: number, resourceId: number, resourceType: EnvironmentResourceType): Promise<void> {
        switch (resourceType) {
            case EnvironmentResourceType.Kubernetes:
                return this._getClient().deleteKubernetesResource(this._getProjectName(), environmentId, resourceId);
            case EnvironmentResourceType.VirtualMachine:
                return this._getClient().deleteVirtualMachineGroup(this._getProjectName(), environmentId, resourceId);
            default:
                return Promise.reject("The specified resource type is not supported.");
        }
    }

    public getServiceEndpoint(endpointId: string): Promise<ServiceEndpoint> {
        return this._getServiceEndpointsClient().getServiceEndpointDetails(this._getProjectName(), endpointId);
    }

    public createServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        this._fillServiceEndpointProjectReferences(endpoint, this._getProjectId());
        return this._getServiceEndpointsClient().createServiceEndpoint(endpoint);
    }
    
    public updateServiceEndpoint(endpoint: ServiceEndpoint, endpointId: string): Promise<ServiceEndpoint> {
        this._fillServiceEndpointProjectReferences(endpoint, this._getProjectId());
        return this._getServiceEndpointsClient().updateServiceEndpoint(endpoint, endpointId);
    }

    public deleteServiceEndpoint(endpointId: string): Promise<void> {
        return this._getServiceEndpointsClient().deleteServiceEndpoint(endpointId, [this._getProjectId()]);
    }

    public getAzureSubscriptions(): Promise<AzureSubscriptionQueryResult> {
        return this._getServiceEndpointsClient().getAzureSubscriptions();
    }

    public executeServiceEndpointRequestResult(serviceEndpointRequest: ServiceEndpointRequest, endpointId: string): Promise<ServiceEndpointRequestResult> {
        return this._getServiceEndpointsClient().executeServiceEndpointRequest(serviceEndpointRequest, this._getProjectName(), endpointId);
    }

    public getAADTenantId(): Promise<string> {
        return this._getTaskAgentClient().getVstsAadTenantId();
    }

    public updateEnvironmentResourceTags (resourceId: number, resourceType: EnvironmentResourceType, newTagsSet: string[]): Promise<EnvironmentResource> {
        const parameters = {
            "resourceId": resourceId.toString(),
            "resourceType": resourceType.toString(),
            "newTagsSet": newTagsSet
        }
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let resourcePromise = contributionService.getDataAsync<EnvironmentResource>(DataProviderIds.EnvironmentResourcesTagUpdateDataProvider, parameters) as Promise<EnvironmentResource>;
        return resourcePromise;
    }

    public deleteEnvironmentResource (resourceId: number, resourceType: EnvironmentResourceType): Promise<void> {
        const parameters = {
            "resourceId": resourceId.toString(),
            "resourceType": resourceType.toString()
        }
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let deleteResourcePromise = contributionService.getDataAsync<EnvironmentResource>(DataProviderIds.EnvironmentDeleteResourceDataProvider, parameters) as Promise<void>;
        
        return deleteResourcePromise;
    }

    private _getProjectName(): string {
        const projectName = PageContextHelper.getProjectName(this.pageContext);
        if (!!projectName) {
            return projectName;
        }

        throw new Error("Release rest client :: Project is not available in page data. Cannot make this network call without project information");
    }

    private _getProjectId(): string {
        const projectId = PageContextHelper.getProjectId(this.pageContext);
        if (!!projectId) {
            return projectId;
        }

        throw new Error("Release rest client :: Project is not available in page data. Cannot make this network call without project information");
    }

    private _getClient(): ITaskAgentRestClient | IEnvironmentsRestClient {
        return getTaskAgentClient(this.pageContext);
    }

    private _getServiceEndpointsClient(): IServiceEndpointRestClient {
        return getServiceEndpointClient(this.pageContext);
    }

    private _getTaskAgentClient(): ITaskAgentRestClient {
        return getTaskAgentClient(this.pageContext);
    }

    private _fillServiceEndpointProjectReferences(endpoint: ServiceEndpoint, projectId: string): void {
        endpoint.serviceEndpointProjectReferences = [
            {
                name: endpoint.name,
                description: endpoint.description,
                projectReference: {
                    id: projectId,
                    name: ""
                }
            }
        ];
    }
    
    public getCommits(top?: string): ICommitsResult | undefined {
        const parameters = {
            top: top ? top : "",
            view: "commits"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data: ICommitsResult | undefined = contributionService.getData<ICommitsResult>(DataProviderIds.EnvironmentRunCommitsDataProvider, parameters);

        if (!data) {
            // checks if data with '{}' parameter key is present.
            data = contributionService.getData(DataProviderIds.EnvironmentRunCommitsDataProvider);
        }

        // check if commits info is present in the data
        if (!!data && !!data.commits) {
            return data;
        }

        return undefined;
    }

    public getCommitsAsync(top?: string): Promise<ICommitsResult> {
        const parameters = {
            top: top ? top : "",
            view: "commits"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<ICommitsResult>(DataProviderIds.EnvironmentRunCommitsDataProvider, parameters, true)! as Promise<ICommitsResult>;
    }

    public getWorkitems(top?: string): IWorkitemsResult | undefined {

        const parameters = {
            top: top ? top : "",
            view: "workitems"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data: IWorkitemsResult | undefined = contributionService.getData<IWorkitemsResult>(DataProviderIds.EnvironmentRunWorkitemsDataProvider, parameters);

        if (!data) {
            // checks if data with '{}' parameter key is present.
            data = contributionService.getData<IWorkitemsResult>(DataProviderIds.EnvironmentRunWorkitemsDataProvider);
        }

        // check if commits info is present in the data
        if (!!data && !!data.workitems) {
            return data;
        }

        return undefined;
    }

    public getWorkitemsAsync(top?: string): Promise<IWorkitemsResult> {

        const parameters = {
            top: top ? top : "",
            view: "workitems"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<IWorkitemsResult>(DataProviderIds.EnvironmentRunWorkitemsDataProvider, parameters, true)! as Promise<IWorkitemsResult>;
    }
}

Services.add("IEnvironmentService", { serviceFactory: EnvironmentService });